import { PropsWithChildren, useEffect, useState } from 'react';
import type { TextProps } from 'react-native';
import { AppState, PixelRatio, Platform, Text } from 'react-native';

import { cn } from '../utils/cn';

export type TextColor = 'default' | 'dark' | 'subtle' | 'link' | 'white';

export const TEXT_TYPES = [
  'titleHero',
  'titleDefault',
  'subtitleDefault',
  'subtitleSmall',
  'bodyRegular',
  'bodyBold',
  'bodySmall',
  'bodySmallBold',
  'buttonDefault',
  'buttonLight',
  'buttonSmall',
  'footnote',
  'overline',
] as const;

export type TextType = (typeof TEXT_TYPES)[number];

export type ThemedTextProps = TextProps & {
  color?: TextColor;
  type?: TextType;
};

const typographyClasses: Record<TextType, string> = {
  titleHero: 'text-login-hero font-semibold leading-login-hero tracking-login-hero font-login-body',
  titleDefault: 'text-login-title font-semibold leading-login-title font-login-body',
  subtitleDefault: 'text-lg font-bold leading-login-subtitle font-login-body',
  subtitleSmall: 'text-base font-bold font-login-body',
  bodyRegular: 'text-base font-normal leading-login-body font-login-body',
  bodyBold: 'text-base font-semibold leading-login-body font-login-body',
  bodySmall: 'text-sm font-normal leading-login-body-sm font-login-body',
  bodySmallBold: 'text-sm font-semibold leading-login-body-sm font-login-body',
  buttonDefault: 'text-base font-semibold leading-login-button tracking-login-button font-login-body',
  buttonLight: 'text-base font-normal tracking-login-button font-login-body',
  buttonSmall: 'text-sm font-semibold tracking-login-button-sm font-login-body',
  footnote: 'text-login-footnote font-medium leading-login-footnote tracking-login-footnote font-login-body',
  overline: 'text-login-footnote font-bold tracking-login-overline uppercase font-login-body',
};

const defaultColorClasses: Partial<Record<TextType, string>> = {
  overline: 'text-login-gray-70',
  footnote: 'text-login-gray-70',
  buttonSmall: 'text-login-purple-50',
  titleHero: 'text-login-purple-90',
  titleDefault: 'text-login-purple-90',
};

const colorOverrideClasses: Record<TextColor, string | undefined> = {
  default: undefined,
  dark: 'text-login-gray-90',
  subtle: 'text-login-gray-50',
  link: 'text-login-purple-50',
  white: 'text-login-surface-pure',
};

export default function ThemedText(props: PropsWithChildren<ThemedTextProps>) {
  // NativeWind's CSS runtime caches styles and won't re-render when the system
  // font scale changes. Keying on fontScale forces a remount when it changes.
  const [fontScale, setFontScale] = useState(PixelRatio.getFontScale());

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        setFontScale(PixelRatio.getFontScale());
      }
    });
    return () => sub.remove();
  }, []);

  const {
    color = 'default',
    type = 'bodyRegular',
    numberOfLines,
    children,
    style,
    className,
    ...rest
  } = props;

  // On native, flexShrink defaults to 0 so text never collapses below its
  // content height. On web, CSS defaults flexShrink to 1, which can shrink
  // the element and then overflow:hidden (applied by numberOfLines) clips it.
  const needsShrinkGuard = Platform.OS === 'web' && numberOfLines != null;

  const colorClass = colorOverrideClasses[color] ?? defaultColorClasses[type] ?? 'text-login-gray-90';

  return (
    <Text
      key={fontScale}
      numberOfLines={numberOfLines}
      className={cn(typographyClasses[type], colorClass, needsShrinkGuard && 'shrink-0', className)}
      style={style}
      {...rest}
    >
      {children}
    </Text>
  );
}
